// path: src/Collection.js
import React, { useMemo, useState } from "react";
import "./App.css";
import characters from "./Characters.json";
import CollectionSortControl from "./Components/CollectionSortControl";
import { getCardImageSrc } from "./utils/cardImages";
import { loadCollectionSortKey, sortCollectionCards } from "./utils/collectionSort";

const normalizeId = (id) => {
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

const getImgSrc = (card) => getCardImageSrc(card);

const SPECIAL_SECTIONS = [
    {
        id: "founders",
        title: "Founders Edition",
        rarity: "founder",
        ariaLabel: "Founders Edition collection",
    },
    {
        id: "alpha",
        title: "Alpha Edition",
        rarity: "alpha",
        ariaLabel: "Alpha Edition collection",
    },
    {
        id: "gems",
        title: "Hidden Gems",
        rarity: "gem",
        ariaLabel: "Hidden Gems collection",
    },
];

const Collection = ({
    unlockedIds = [],
    foilUnlockedIds = [],
    selectedProfileImageId = null,
    pickProfile = false,
    onSelectProfileImage,
    title = "Collection",
    embedded = false,
}) => {
    const [previewCard, setPreviewCard] = useState(null);
    const [sortKey, setSortKey] = useState(() => loadCollectionSortKey());
    const [foilsOnly, setFoilsOnly] = useState(false);

    const unlockedSet = useMemo(() => {
        return new Set((unlockedIds || []).map(normalizeId));
    }, [unlockedIds]);

    const foilUnlockedSet = useMemo(() => {
        return new Set((foilUnlockedIds || []).map(normalizeId));
    }, [foilUnlockedIds]);

    const { baseCards, specialSections } = useMemo(() => {
        const base = [];
        const byRarity = {
            founder: [],
            alpha: [],
            gem: [],
        };

        characters.forEach((card) => {
            const rarity = card?.rarity;
            if (rarity && byRarity[rarity]) {
                byRarity[rarity].push(card);
            } else if (!rarity) {
                base.push(card);
            }
        });

        return {
            baseCards: base,
            specialSections: SPECIAL_SECTIONS.map((section) => ({
                ...section,
                cards: byRarity[section.rarity] || [],
            })),
        };
    }, []);

    const sortedBaseCards = useMemo(
        () => sortCollectionCards(baseCards, sortKey),
        [baseCards, sortKey]
    );

    const sortedSpecialSections = useMemo(
        () =>
            specialSections.map((section) => ({
                ...section,
                cards: sortCollectionCards(section.cards, sortKey),
            })),
        [specialSections, sortKey]
    );

    const visibleBaseCards = useMemo(() => {
        if (!foilsOnly) {
            return sortedBaseCards;
        }
        return sortedBaseCards.filter((card) =>
            foilUnlockedSet.has(normalizeId(card.id))
        );
    }, [foilUnlockedSet, foilsOnly, sortedBaseCards]);

    const visibleSpecialSections = useMemo(() => {
        if (!foilsOnly) {
            return sortedSpecialSections;
        }
        return sortedSpecialSections.map((section) => ({
            ...section,
            cards: section.cards.filter((card) =>
                foilUnlockedSet.has(normalizeId(card.id))
            ),
        }));
    }, [foilUnlockedSet, foilsOnly, sortedSpecialSections]);

    const total = characters.length;

    const unlockedCount = characters.reduce((acc, c) => {
        const id = normalizeId(c.id);
        return acc + (unlockedSet.has(id) ? 1 : 0);
    }, 0);

    const foilUnlockedCount = characters.reduce((acc, c) => {
        const id = normalizeId(c.id);
        return acc + (foilUnlockedSet.has(id) ? 1 : 0);
    }, 0);

    const countUnlockedIn = (cards) =>
        cards.reduce((acc, card) => {
            return acc + (unlockedSet.has(normalizeId(card.id)) ? 1 : 0);
        }, 0);

    const baseUnlockedCount = countUnlockedIn(baseCards);

    const closePreview = () => {
        setPreviewCard(null);
    };

    const previewCardIsFoil =
        previewCard && foilUnlockedSet.has(normalizeId(previewCard.id));

    const selectedId = normalizeId(selectedProfileImageId);

    const renderCard = (c) => {
        const id = normalizeId(c.id);
        const unlocked = unlockedSet.has(id);
        const foilUnlocked = foilUnlockedSet.has(id);
        const isSelected = selectedId != null && id === selectedId;
        const special = Boolean(c?.rarity);
        const displayLabel = special
            ? String(c.name || "").trim() || `#${id}`
            : `#${id}`;

        return (
            <div
                className={`collectionItem${
                    isSelected ? " collectionItem--selected" : ""
                }`}
                role="listitem"
                key={c.id}
            >
                <button
                    type="button"
                    className={`collectionImageButton ${
                        unlocked ? "collectionImageButton--unlocked" : ""
                    }${foilUnlocked ? " collectionImageButton--foil" : ""}`}
                    onClick={() => {
                        if (!unlocked) {
                            return;
                        }
                        if (pickProfile && typeof onSelectProfileImage === "function") {
                            onSelectProfileImage(id);
                            return;
                        }
                        setPreviewCard(c);
                    }}
                    disabled={!unlocked}
                    aria-label={
                        unlocked
                            ? pickProfile
                                ? `Use ${displayLabel} as profile picture`
                                : `View larger image of ${displayLabel}`
                            : `${displayLabel} locked`
                    }
                >
                    <div
                        className={
                            foilUnlocked ? "collectionImageFoilWrap" : ""
                        }
                    >
                        <img
                            src={getImgSrc(c)}
                            alt={
                                foilUnlocked
                                    ? `${displayLabel} foil version unlocked`
                                    : unlocked
                                    ? `${displayLabel} unlocked`
                                    : `${displayLabel} locked`
                            }
                            className={`collectionImage ${
                                unlocked
                                    ? "collectionImage--unlocked"
                                    : "collectionImage--locked"
                            }`}
                            loading="lazy"
                        />
                    </div>
                </button>

                <div
                    className="collectionName"
                    title={special ? displayLabel : undefined}
                >
                    {displayLabel}
                </div>
            </div>
        );
    };

    return (
        <div className={`collectionScreen${embedded ? " collectionScreen--embedded" : ""}`}>
            {!embedded ? (
                <div className="collectionHeader">
                    <h1 className="impactFont pageTitle collectionTitle">
                        {pickProfile ? "Choose Profile Picture" : title}
                    </h1>
                    <div className="collectionProgress">
                        Regular: {unlockedCount}/{total} | Foil:{" "}
                        {foilUnlockedCount}/{total}
                    </div>
                </div>
            ) : (
                <div className="collectionProgress collectionProgress--embedded">
                    Regular: {unlockedCount}/{total} | Foil: {foilUnlockedCount}/
                    {total}
                </div>
            )}

            <CollectionSortControl
                id="collection-sort"
                value={sortKey}
                onChange={setSortKey}
            />

            <div className="collectionFilterRow" aria-label="Collection filters">
                <button
                    type="button"
                    className={`collectionFilterButton${
                        foilsOnly ? " collectionFilterButton--active" : ""
                    }`}
                    aria-pressed={foilsOnly}
                    onClick={() => setFoilsOnly((value) => !value)}
                >
                    {foilsOnly ? "Showing: Foils only" : "Filter: All cards"}
                </button>
            </div>

            <section
                className="collectionSection"
                aria-labelledby="collection-base-heading"
            >
                <div className="collectionSectionHeader">
                    <h2
                        id="collection-base-heading"
                        className="impactFont collectionSectionTitle"
                    >
                        Base Corgis
                    </h2>
                    <div className="collectionSectionProgress">
                        {baseUnlockedCount}/{baseCards.length}
                    </div>
                </div>

                <div
                    className="collectionGrid"
                    role="list"
                    aria-label="Base Corgi collection"
                >
                    {visibleBaseCards.map(renderCard)}
                </div>
            </section>

            {visibleSpecialSections.map((section) => (
                <section
                    key={section.id}
                    className="collectionSection"
                    aria-labelledby={`collection-${section.id}-heading`}
                >
                    <div className="collectionSectionHeader">
                        <h2
                            id={`collection-${section.id}-heading`}
                            className="impactFont collectionSectionTitle"
                        >
                            {section.title}
                        </h2>
                        <div className="collectionSectionProgress">
                            {countUnlockedIn(section.cards)}/{section.cards.length}
                        </div>
                    </div>

                    <div
                        className="collectionGrid"
                        role="list"
                        aria-label={section.ariaLabel}
                    >
                        {section.cards.map(renderCard)}
                    </div>
                </section>
            ))}

            {previewCard && (
                <div
                    className="collectionPreviewOverlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${previewCard.name || `#${normalizeId(previewCard.id)}`} enlarged card preview`}
                    onClick={closePreview}
                >
                    <div
                        className="collectionPreviewPanel"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div
                            className={
                                previewCardIsFoil
                                    ? "collectionPreviewFoilWrap collectionImageFoilWrap"
                                    : "collectionPreviewFoilWrap"
                            }
                        >
                            <img
                                src={getImgSrc(previewCard)}
                                alt={`${previewCard.name || `#${normalizeId(previewCard.id)}`} enlarged`}
                                className="collectionPreviewImage"
                            />
                        </div>

                        <div className="collectionPreviewName">
                            {previewCard.name}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Collection;
